import eventBus from '@/helpers/eventBusHelper';

// Gate debug popups via localStorage or env var
/**
 * Checks whether debug popups are currently enabled, via the `debugPopups`
 * localStorage flag or the `VITE_DEBUG_POPUPS` env var as a fallback.
 * @returns {boolean} true if debug popups should be shown
 */
const isEnabled = () => {
  try {
    const v = localStorage.getItem('debugPopups') ?? import.meta.env.VITE_DEBUG_POPUPS;
    return ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
  } catch {
    return false;
  }
};

/**
 * Persists the debug-popups on/off flag to localStorage.
 * @param {boolean} [enabled=true] - Whether debug popups should be enabled
 */
export function setDebugPopupsEnabled(enabled = true) {
  try { localStorage.setItem('debugPopups', enabled ? '1' : '0'); } catch {}
}

/**
 * Dispatch a debug notification (shown as a PrimeVue toast via DebugNotifier)
 * @param {string} summary - short title
 * @param {any} detailOrOptions - string/object detail or full toast options
 * @param {object} [options] - additional toast options
 */
export function debugNotify(summary, detailOrOptions, options = {}) {
  if (!isEnabled()) return;
  let payload = {
    severity: 'info',
    summary: String(summary ?? 'Debug'),
    life: 12000, // longer default so you can read it
    closable: true,
  };
  if (typeof detailOrOptions === 'string') {
    payload.detail = detailOrOptions;
  } else if (detailOrOptions && typeof detailOrOptions === 'object') {
    // Pretty print objects by default
    const { detail, ...rest } = detailOrOptions;
    payload = { ...payload, ...rest };
    if (detail != null) {
      payload.detail = typeof detail === 'string' ? detail : safeStringify(detail);
    }
  }
  if (options && typeof options === 'object') {
    payload = { ...payload, ...options };
  }
  // Sticky option support: if sticky is set, extend life significantly
  if (payload.sticky === true) {
    payload.life = 60000; // 60s
  }
  // Ensure detail is a string for toast
  if (payload.detail == null) payload.detail = '';
  if (typeof payload.detail !== 'string') payload.detail = safeStringify(payload.detail);
  try {
    eventBus.emit('debug', payload);
  } catch {}
}

/**
 * Convenience wrapper around `debugNotify` for showing an arbitrary object,
 * pretty-printed as its detail.
 * @param {string} label - Toast summary/title
 * @param {any} obj - Object to pretty-print in the toast detail
 */
export function debugNotifyJSON(label, obj) {
  if (!isEnabled()) return;
  debugNotify(String(label || 'Debug'), { detail: obj });
}

/**
 * JSON.stringify with Error-aware serialization, falling back to String()
 * if stringification fails (e.g. circular references).
 * @param {any} obj - Value to stringify
 * @returns {string} pretty-printed JSON, or the value's string form on failure
 */
function safeStringify(obj) {
  try { return JSON.stringify(obj, replacer, 2); } catch { return String(obj); }
}
/**
 * JSON.stringify replacer that serializes Error instances as plain objects
 * (name/message/stack) instead of `{}`.
 * @param {string} key - Current key being serialized
 * @param {any} value - Current value being serialized
 * @returns {any} the value to serialize in place of the original
 */
function replacer(key, value) {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  return value;
}

// Convenience globals for quick toggling/testing in DevTools
if (typeof window !== 'undefined') {
  window.debugNotify = debugNotify;
  window.__debugPopups = (enable = true) => setDebugPopupsEnabled(!!enable);
}

export default debugNotify;
